import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../store/AuthContext";
import { getVideos } from "../../api/videosApi";

import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  CircularProgress,
  Alert,
  CardMedia,
  Button,
  useTheme
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useSearchParams, useNavigate } from "react-router-dom";
import { getClasses, getSubjects } from "../../api/classSubjectApi";
import { getTopics } from "../../api/topicsApi"; // NEW

export default function StudentVideos() {
  const { token } = useContext(AuthContext);
  const [isWeb, setIsWeb] = useState([]);

  const [videos, setVideos] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]); // NEW
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const classId = params.get("class_id");
  const subjectId = params.get("subject_id");
  const topicId = params.get("topic_id"); // NEW

  useEffect(() => {
    setIsWeb(localStorage.getItem("logintype"));
  }, []);

  // LOAD CLASSES
  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const res = await getClasses(token);
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Failed to load classes.");
    }
  };

  // LOAD SUBJECTS
  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    try {
      const res = await getSubjects(token);
      setSubjects(res.data);
    } catch {
      setError("Failed to load subjects.");
    }
  };

  // LOAD TOPICS
  useEffect(() => {
    loadTopics();
  }, [classId, subjectId]);

  const loadTopics = async () => {
    try {
      const res = await getTopics(token);
      const filtered = res.data.filter(
        (t) => t.class_id == classId && t.subject_id == subjectId
      );
      setTopics(filtered);
    } catch {
      setError("Failed to load topics.");
    }
  };

  // LOAD VIDEOS
  useEffect(() => {
    loadVideos();
  }, [classId, subjectId, topicId]);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const res = await getVideos(token, {
        class_id: classId,
        subject_id: subjectId,
        topic_id: topicId
      });
      setVideos(res.data);
    } catch {
      setError("Failed to load videos.");
    } finally {
      setLoading(false);
    }
  };

  const getThumbnail = (url) => {
    const match = url?.match(/(?:v=|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
  };

  const openVideo = (video) => {
    navigate(
      `/student/watch?url=${encodeURIComponent(video.file_path)}&title=${encodeURIComponent(
        video.title
      )}`
    );
  };

  const selectedClass = classes.find((c) => String(c.id) === String(classId));
  const selectedSubject = subjects.find((s) => String(s.id) === String(subjectId));
  const selectedTopic = topics.find((t) => String(t.id) === String(topicId)); // NEW

  const goToTopicPage = () => {
    if (isWeb === "sso") {
      navigate(`/student/select-topic-sso?class_id=${classId}&subject_id=${subjectId}`, {
        replace: true
      });
    }
    else {
      navigate(`/student/select-topic?class_id=${classId}&subject_id=${subjectId}`, {
        replace: true
      });
    }
  };

  // LOADING
  if (loading) {
    return (
      <Box
        sx={{
          background: "#000",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column"
        }}
      >
        <CircularProgress size={50} />
        <Typography mt={2} color="#ccc">
          Loading Videos...
        </Typography>
      </Box>
    );
  }

  // ERROR
  if (error) {
    return (
      <Container sx={{ background: "#000", minHeight: "100vh", py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ background: "#000", minHeight: "100vh", py: 3 }}>
      <Container maxWidth="xl">

        {/* BACK BUTTON */}
        <Box sx={{ textAlign: "center", mb: 4, position: "relative" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goToTopicPage}
            variant="outlined"
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#4f8ef7",
              borderColor: "#4f8ef7",
              "&:hover": {
                borderColor: "#d24df2",
                backgroundColor: "rgba(79, 142, 247, 0.1)"
              }
            }}
          >
            Topics
          </Button>

          {/* PAGE TITLE */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              mb: 3,
              background: "linear-gradient(45deg, #4f8ef7, #d24df2)",
              WebkitBackgroundClip: "text",
              color: "transparent",
              textAlign: "center"
            }}
          >
            Class {selectedClass?.class || "Class"} —{" "}
            {selectedSubject?.subject || "Subject"} —{" "}
            {selectedTopic?.topic_name || "Topic"}
          </Typography>
        </Box>

        {/* VIDEO GRID */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "center"
          }}
        >
          {videos.map((video) => {
            const thumb = getThumbnail(video.file_path);

            return (
              <Card
                key={video.id}
                onClick={() => openVideo(video)}
                sx={{
                  width: {
                    xs: "100%",
                    sm: "48%",
                    md: "31%",
                    lg: "23%"
                  },
                  background: "#111",
                  borderRadius: 2,
                  border: "1px solid #222",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "0.25s",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: "0 0 15px rgba(255,255,255,1)"
                  }
                }}
              >
                <CardMedia
                  sx={{
                    height: { xs: 150, sm: 160, md: 170, lg: 180 },
                    background: thumb
                      ? `url(${thumb}) center/cover`
                      : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                  }}
                />

                <CardContent sx={{ p: 1.5 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      color: "#fff",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      minHeight: 40
                    }}
                  >
                    {video.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#999",
                      mt: 0.5,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      minHeight: 32
                    }}
                  >
                    {video.description || "Video lesson"}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* NO VIDEOS */}
        {videos.length === 0 && (
          <Box textAlign="center" py={8}>
            <Typography color="#aaa">No videos available</Typography>
          </Box>
        )}

        {/* BOTTOM BUTTON */}
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            variant="contained"
            onClick={goToTopicPage}
            sx={{
              background: "linear-gradient(45deg, #4f8ef7, #d24df2)",
              color: "#fff",
              padding: "10px 30px",
              fontWeight: "bold",
              fontSize: "1rem",
              "&:hover": {
                background: "linear-gradient(45deg, #3a7cf0, #c13ce5)",
                transform: "translateY(-2px)",
                boxShadow: "0 5px 15px rgba(79,142,247,0.4)"
              }
            }}
          >
            Back to Topics
          </Button>
        </Box>

      </Container>
    </Box>
  );
}
